/* eslint-disable @typescript-eslint/camelcase */
import dayjs from 'dayjs';
import last from 'lodash/last';
import { formatDate } from 'modules/utils/DateUtils';
import { ImageSourcePropType } from 'react-native';
import Config from 'react-native-config';
import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import formatMoney from 'services/formatMoney';
import { GetHighPriorityActionItems as GetHighPriorityActionItemsQuery } from 'services/orchestration/__generated__/GetHighPriorityActionItems';
import { GetLowPriorityActionItems as GetLowPriorityActionItemsQuery } from 'services/orchestration/__generated__/GetLowPriorityActionItems';
import {
  HighPriorityActionItemsTypesInterface,
  LowPriorityActionItemsTypesInterface
} from 'shared/src/store/priorityItems/types';
import { ProviderSummary } from 'store/findProvider/types';
import { VisitSummary } from 'store/visitSummary/types';
import { Color } from 'modules/styles/colors';
import {
  AdvisoryType,
  BookingTypes,
  ClinicTypes,
  ItemTypes,
  QueryPathReadableName,
  QueryPathReadableNameKey,
  RTARelatedData
} from './types';

export const hasType = (from: string | null, to: string | null) => {
  if (!from || !to) return false;

  const camelFrom = from.split(/\s+/g).join('');
  const camelTo = to.split(/\s+/g).join('');

  return camelFrom.toLowerCase().includes(camelTo.toLowerCase());
};

export const findFromAdvisoryTypes = (
  type: typeof BookingTypes | typeof ClinicTypes,
  from: string | null
) => {
  return Object.values(type).find(to => hasType(from, to));
};

export const findFromVisitTypes = (types: any[], to: string) => {
  return types.find(type => hasType(type.visitTypeCode, to));
};

const onAnalyticsClick = (
  cardTitle: string,
  label: string,
  cardCategory: string,
  ctaLink?: string
) => {
  const detailsViewedEventData: AmplitudeEventData = {
    card_title: cardTitle,
    card_category: cardCategory,
    cta_text: label,
    currentUrl: '/u/dashboard',
    referringUrl: '/u/dashboard'
  };
  if (ctaLink) {
    detailsViewedEventData.cta_link = ctaLink;
  }

  analyticsService.logEvent(AnalyticsEvent.DashboardToDoCardClicked, detailsViewedEventData);
};
const onAnalyticsDismissedClick = (
  cardTitle: string,
  cardCategory: string,
  snoozedFor?: number | boolean
) => {
  const detailDismissedEventData: AmplitudeEventData = {
    card_title: cardTitle,
    currentUrl: '/u/dashboard',
    referringUrl: '/u/dashboard',
    card_category: cardCategory,
    dismissal_type: snoozedFor ? `Snoozed for ${snoozedFor} Days` : 'Dismissed'
  };

  analyticsService.logEvent(AnalyticsEvent.DashboardToDoCardDismissed, detailDismissedEventData);
};

export const getHealthAdvisoryActionProps = (
  advisoryType: AdvisoryType,
  actions: { [x: string]: () => void }
) => {
  const { onMammogramPress, onFluPress, onBookingPress, onSubmitAdvisory } = actions;

  // Default Health Advisory Action Props
  let result: {
    label: string;
    onPress: () => any;
    onSubmit: () => any;
    submitText?: string;
    onAnalyticsClick: () => () => any;
    onAnalyticsDismissedClick: (snoozedFor?: number | boolean) => () => any;
  } = {
    label: '',
    onPress: () => false,
    onSubmit: () => false,
    onAnalyticsClick: () => () => {},
    onAnalyticsDismissedClick: () => () => {}
  };

  // Booking Type Health Advisory Action Props
  if (advisoryType === AdvisoryType.WELLNESS) {
    result = {
      label: 'Book appointment',
      onPress: onBookingPress,
      onSubmit: onSubmitAdvisory,
      onAnalyticsClick: () =>
        onAnalyticsClick(advisoryType, 'Book appointment', 'Health Advisories'),
      onAnalyticsDismissedClick: snoozedFor =>
        onAnalyticsDismissedClick(advisoryType, 'Health Advisories', snoozedFor)
    };
  }

  if (advisoryType === AdvisoryType.CERVICAL_CANCER) {
    result = {
      label: 'Book appointment',
      onPress: onBookingPress,
      onSubmit: onSubmitAdvisory,
      onAnalyticsClick: () =>
        onAnalyticsClick(advisoryType, 'Book appointment', 'Health Advisories'),
      onAnalyticsDismissedClick: snoozedFor =>
        onAnalyticsDismissedClick(advisoryType, 'Health Advisories', snoozedFor)
    };
  }

  // Diabetes Type Health Advisory Action Props
  if (advisoryType === AdvisoryType.DIABETES_A1C) {
    result = {
      label: 'Book appointment',
      onPress: onBookingPress,
      onSubmit: onSubmitAdvisory,
      onAnalyticsClick: () =>
        onAnalyticsClick(advisoryType, 'Book appointment', 'Health Advisories'),
      onAnalyticsDismissedClick: snoozedFor =>
        onAnalyticsDismissedClick(advisoryType, 'Health Advisories', snoozedFor)
    };
  }

  // Clinic Type (Mammogram) Health Advisory Action Props
  if (advisoryType === AdvisoryType.MAMMOGRAM) {
    result = {
      label: 'Call to schedule',
      onPress: onMammogramPress,
      onSubmit: onSubmitAdvisory,
      onAnalyticsClick: () =>
        onAnalyticsClick(advisoryType, 'Call to schedule', 'Health Advisories'),
      onAnalyticsDismissedClick: snoozedFor =>
        onAnalyticsDismissedClick(advisoryType, 'Health Advisories', snoozedFor)
    };
  }

  // Clinic Type (Influenza (flu) Vaccine) Health Advisory Action Props
  if (advisoryType === AdvisoryType.FLU) {
    result = {
      label: 'Find location',
      onPress: onFluPress,
      onSubmit: onSubmitAdvisory,
      submitText: 'If already received, submit information',
      onAnalyticsClick: () => onAnalyticsClick(advisoryType, 'Find location', 'Health Advisories'),
      onAnalyticsDismissedClick: snoozedFor =>
        onAnalyticsDismissedClick(advisoryType, 'Health Advisories', snoozedFor)
    };
  }
  if (advisoryType === AdvisoryType.HPV_VACCINE) {
    result = {
      label: 'Book appointment',
      onPress: onBookingPress,
      onSubmit: onSubmitAdvisory,
      submitText: 'Submit vaccine record',
      onAnalyticsClick: () =>
        onAnalyticsClick(advisoryType, 'Book appointment', 'Health Advisories'),
      onAnalyticsDismissedClick: snoozedFor =>
        onAnalyticsDismissedClick(advisoryType, 'Health Advisories', snoozedFor)
    };
  }
  return result;
};

export const formatActionItemQueryError = (low: any[] = [], high: any[] = []) => {
  const results = [];

  for (const item of [...low, ...high]) {
    const found = Object.keys(QueryPathReadableName).find(
      key => key === last(item.path)
    ) as Nullable<QueryPathReadableNameKey>;

    if (found) {
      results.push(QueryPathReadableName[found]);
    }
  }

  return results;
};

// getActionItems includes __typeName: 'ActionItems' which needs to be filtered out
export const queryHasData = (
  queryObj?: GetHighPriorityActionItemsQuery | GetLowPriorityActionItemsQuery
) => {
  if (!queryObj) {
    return false;
  }

  return Object.values(queryObj?.getActionItems ?? {}).some(
    value => !!value && value !== 'ActionItems'
  );
};

// ********************************
// ** SETS UP ITEMS FOR CAROUSEL **
// ********************************

const getCardOrder = (type: ItemTypes) => {
  switch (type) {
    case ItemTypes.ONBOARDING:
      return 0;
    case ItemTypes.ANNOUNCEMENT:
      return 1;
    case ItemTypes.ANTICOAGULATION:
      return 2;
    case ItemTypes.RTA_SUMMARY:
      return 3;
    case ItemTypes.HEALTH_ADVISORY:
      return 4;
    case ItemTypes.GOALS:
      return 5;
    case ItemTypes.NO_DATA:
      return 6;
    case ItemTypes.ERROR:
      return 7;
    default:
      return 8;
  }
};
export const getItems = (
  lowPriorityActionItems: LowPriorityActionItemsTypesInterface,
  highPriorityActionItems: HighPriorityActionItemsTypesInterface,
  excludeAnticoagulation?: string | null,
  isImpersonating = false,
  showOnboardingCard = false,
  showNeedActivation = false,
  pendingEVisits: VisitSummary[] = [],
  completedEVisits: VisitSummary[] = []
) => {
  const result = [];

  // If both errored return one card/dedupe
  if (highPriorityActionItems) {
    const { antiCoagulation, healthAdvisories } = highPriorityActionItems;

    if (antiCoagulation) {
      // excludeAnticoagulation should always either be a valid string id, or null
      if (excludeAnticoagulation === null || antiCoagulation.eventId !== excludeAnticoagulation) {
        result.push({
          type: ItemTypes.ANTICOAGULATION,
          payload: {
            ...antiCoagulation,
            onAnalyticsClick: () =>
              onAnalyticsClick(
                'You have a new anti-coagulation alert',
                'View Details',
                'Anticoagulation'
              )
          }
        });
      }
    }

    // @Note: Announcement cards are still under review for the new dashboard and
    // will be reintroduced in phase two of the new dashboard redesign.
    // if (announcements) {
    //   announcements.forEach(card => {
    //     const relatedData = JSON.parse(card.relatedData);
    //     result.push({
    //       type: ItemTypes.ANNOUNCEMENT,
    //       payload: {
    //         eventTypeId: card.eventTypeId,
    //         id: card.id,
    //         relatedData,
    //         sourceId: card.sourceId,
    //         onAnalyticsClick: () =>
    //           onAnalyticsClick(
    //             relatedData.notificationText,
    //             relatedData.buttonText,
    //             'Announcements',
    //             relatedData.externalLink
    //           ),
    //         onAnalyticsDismissedClick: () =>
    //           onAnalyticsDismissedClick(relatedData.notificationText, 'Announcements')
    //       }
    //     });
    //   });
    // }

    if (healthAdvisories) {
      for (const advisory of healthAdvisories) {
        if (advisory?.advisoryType in AdvisoryType) {
          if (advisory?.advisoryType !== AdvisoryType.WELLNESS) {
            result.push({ type: ItemTypes.HEALTH_ADVISORY, payload: advisory });
          }
        }
      }
    }
  }

  if (lowPriorityActionItems) {
    const { goals, rtas } = lowPriorityActionItems;

    if ((goals?.activeCount ?? 0) > 0) {
      result.push({
        type: ItemTypes.GOALS,
        payload: {
          onAnalyticsClick: () =>
            onAnalyticsClick('You have new or updated goals available', 'View Details', 'Goals')
        }
      });
    }

    if (!isImpersonating && rtas) {
      rtas.forEach(card => {
        const relatedData = JSON.parse(card?.relatedData);
        result.push({
          type: ItemTypes.RTA_SUMMARY,
          payload: {
            eventTypeId: card.eventTypeId,
            id: card.id,
            relatedData,
            sourceId: card.sourceId,
            onAnalyticsClick: () =>
              onAnalyticsClick('You have an upcoming bill.', 'View Details', 'RTA'),
            onAnalyticsDismissedClick: () =>
              onAnalyticsDismissedClick('Your have an upcoming bill', 'RTA')
          }
        });
      });
    }
  }

  if (showNeedActivation) {
    result.push({ type: ItemTypes.NEED_ACTIVATION });
  }

  if (showOnboardingCard) {
    result.push({ type: ItemTypes.ONBOARDING });
  }

  if (!!pendingEVisits.length && Config.EVISITS === 'enabled') {
    pendingEVisits.forEach(item => {
      result.push({
        type: ItemTypes.PENDING_EVISITS,
        payload: {
          ...item,
          onAnalyticsClick: () =>
            onAnalyticsClick('Your E-Visit results are available', 'View Details', 'E-Vist'),
          onAnalyticsDismissedClick: () =>
            onAnalyticsDismissedClick('Your E-Visit results are available', 'E-Vist')
        }
      });
    });
  }
  if (!!completedEVisits.length && Config.EVISITS === 'enabled') {
    completedEVisits.forEach(item => {
      result.push({
        type: ItemTypes.COMPLETED_EVISITS,
        payload: {
          ...item,
          onAnalyticsClick: () =>
            onAnalyticsClick('Your E-Visit results are available', 'View Details', 'E-Vist'),
          onAnalyticsDismissedClick: () =>
            onAnalyticsDismissedClick('Your E-Visit results are available', 'E-Vist')
        }
      });
    });
  }
  // TOOD: add error card in next iteration
  // if (highPriorityQuery.error || lowPriorityQuery.error) {
  //   const errorItem: ErrorItem = {
  //     type: ItemTypes.ERROR,
  //     payload: formatActionItemQueryError(
  //       highPriorityQuery?.error?.graphQLErrors,
  //       lowPriorityQuery?.error?.graphQLErrors
  //     )
  //   };
  //   // If the user only has errors, then show the no data card first for a better experience.
  //   if (result.length === 0) {
  //     const noDataItem: NoDataItem = {
  //       type: ItemTypes.NO_DATA
  //     };
  //     result.push(noDataItem);
  //   }
  //   result.push(errorItem);
  // }
  result.sort((a, b) => (getCardOrder(a.type) > getCardOrder(b.type) ? 1 : -1));

  return result;
};

// Sometimes selecthealth comes as a string Y/N value vs a boolean
const isTruthy = (value: any, comparator: string) => {
  if (typeof value === 'boolean') {
    return !!value;
  }

  if (typeof value === 'string') {
    return value.toLowerCase() === comparator.toLowerCase();
  }

  return false;
};

export const getPrimaryCareProvider = (providers: ProviderSummary[]): ProviderSummary | null => {
  if (!providers.length) {
    return null;
  }

  return providers.find(p => isTruthy(p.isSelectHealth, 'y')) ?? providers[0];
};

export const getAnticoagulationResponseDateText = (createdAt: string) => {
  const now = new Date();
  const responseDate = dayjs(createdAt).add(3, 'day');
  const prettyDate = responseDate.format('MM/DD/YYYY');
  const numberDayDifference = responseDate.diff(now, 'day');

  return numberDayDifference >= 1
    ? `Please respond within ${numberDayDifference} day(s), by ${prettyDate}`
    : `Please respond by ${prettyDate}`;
};

export const healthAdvisoryTypeForName = (healthAdvisoryName: string) => {
  const bookingTypes = Object.values(BookingTypes);
  const clinicTypes = Object.values(ClinicTypes);
  return [...bookingTypes, ...clinicTypes].find(type => healthAdvisoryName.startsWith(type));
};

export enum DBColors {
  orange = '#FFB768',
  lightBlue = '#B6EDF6',
  lightGreen = '#DBF3D2',
  dimBlue = '#AED3EF',
  dimGreen = '#93E4D8',
  pink = '#FBC0D1',
  red = '#FFA689',
  white = '#F2F5F9',
  purple = '#DDD0FC',
  blue = '#36B7E8',
  yellow = '#FAE780',
  grey = '#E4E4E4'
}

export interface AdvisoryObject {
  title: string;
  image?: ImageSourcePropType;
  color?: string;
  hOffset?: number;
  vOffset?: number;
}

export const titleForHealthAdvisoryType = (advisoryType: AdvisoryType): string => {
  switch (advisoryType) {
    case AdvisoryType.FLU:
      return "It's time to get your Influenza (Flu) Vaccine";
    case AdvisoryType.MAMMOGRAM:
      return "It's time to schedule your Mammogram";
    case AdvisoryType.DIABETES_A1C:
      return "It's time to schedule a diabetes follow-up appointment";
    case AdvisoryType.CERVICAL_CANCER:
      return "It's time to schedule your Cervical Cancer screening";
    // @Note: wellness cards are under review for phase two of the dashboard redesign
    // case AdvisoryType.WELLNESS:
    //   return "It's time to schedule your annual appointment";
    case AdvisoryType.HPV_VACCINE:
      return "It's time to schedule your HPV vaccine";
    default:
      return 'Health Advisory';
  }
};

export const MOCKED_DASHBOARD_ITEMS = [
  {
    type: 'error',
    payload: {}
  },
  {
    type: 'anticoagulation',
    payload: {
      eventId: 'e09929ce-e454-47f0-bdd0-bdbfb7e2c93f',
      onAnalyticsClick: () => {},
      sourceId: '405100772',
      createdAt: '2021-10-22T18:26:21.000Z',
      __typename: 'AntiCoagulation'
    }
  },
  {
    type: 'healthAdvisory',
    payload: {
      eventId: 'c1341e22-0880-4383-97b7-a29e45bc9772',
      advisoryName: 'INFLUENZA (FLU) VACCINE',
      advisoryType: 'FLU',
      advisoryReferenceCD: 'FLU_VACCINE_9',
      snoozeOptions: {
        days: [7, 30],
        nextSeason: true,
        __typename: 'SnoozeOptions'
      },
      __typename: 'HealthAdvisory'
    }
  },
  {
    type: 'healthAdvisory',
    payload: {
      eventId: '6eee5a16-e2a2-4ecc-89ef-b7288d3d03f3',
      advisoryName: 'HPV VACCINE',
      advisoryType: 'HPV_VACCINE',
      advisoryReferenceCD: 'HUMAN PAPILLOMAVIRUS VACCINATION',
      snoozeOptions: {
        days: [7, 30, 90],
        nextSeason: false,
        __typename: 'SnoozeOptions'
      },
      __typename: 'HealthAdvisory'
    }
  },
  {
    type: 'healthAdvisory',
    payload: {
      eventId: '9495bab4-ec3d-414b-8b4a-4271c1749b1f',
      advisoryName: 'DIABETES HBA1C MONITORING',
      advisoryType: 'DIABETES_A1C',
      advisoryReferenceCD: 'ELEVATED HBA1C MONITORING',
      snoozeOptions: {
        days: [7, 30],
        nextSeason: false,
        __typename: 'SnoozeOptions'
      },
      __typename: 'HealthAdvisory'
    }
  },
  {
    type: 'healthAdvisory',
    payload: {
      eventId: 'a82381d9-cd9a-4819-82fd-7969993a9a73',
      advisoryName: 'CERVICAL CANCER SCREENING',
      advisoryType: 'CERVICAL_CANCER',
      advisoryReferenceCD: 'CERVICAL CANCER SCREENING',
      snoozeOptions: {
        days: [7, 30, 90],
        nextSeason: false,
        __typename: 'SnoozeOptions'
      },
      __typename: 'HealthAdvisory'
    }
  },
  {
    type: 'healthAdvisory',
    payload: {
      eventId: '9a1b1f67-4a30-465f-8dc9-d16813b54e64',
      advisoryName: 'MAMMOGRAM',
      advisoryType: 'MAMMOGRAM',
      advisoryReferenceCD: 'BREAST CANCER SCREENING',
      snoozeOptions: {
        days: [7, 30, 90],
        nextSeason: false,
        __typename: 'SnoozeOptions'
      },
      __typename: 'HealthAdvisory'
    }
  },
  {
    type: 'onboarding',
    payload: {}
  },
  {
    type: 'needActivation',
    payload: {}
  },
  {
    type: 'goals',
    payload: {}
  },
  {
    type: 'pending',
    payload: {
      locationObject: {
        locationId: '16588797757',
        locationNcid: null,
        locationName: 'CCCL_EVisit'
      },
      primaryProvider: {
        providerName: 'Homer Warner MD',
        providerId: '3930875',
        corpProvId: '479',
        providerNcid: null,
        providerType: 'Attending Physician',
        providerTypeNcid: null,
        providerSpecialty: null,
        providerImageUrl: null,
        providerPhoneNumber: null,
        isSelectHealth: false,
        isAttendingPhysician: false
      },
      status: 'in-progress',
      visitType: 'outpatient',
      chiefComplaint: null,
      fin: '7380500487',
      lastUpdated: '2021-08-20T14:52:48.000Z',
      _id: '6177416eab3d5e02a503f3de',
      source: 'fhir',
      id: '112989544',
      patient: 'XZTESTPHC, PORTAL DISNEYLAND',
      date: '2021-08-20T14:51:39.000Z',
      notInPatientPortal: false,
      visitDescription: 'Patient visit'
    }
  }
];

export const calculateRTACardAmount = (relatedData: RTARelatedData) => {
  if (!relatedData) return 0;

  const { claimInformation } = relatedData;
  if (!claimInformation) return 0;

  const {
    totalDeductibleAmount = 0,
    totalCoinsuranceAmount = 0,
    totalCopaymentAmount = 0,
    totalNonCoveredAmount = 0
  } = claimInformation;

  return Math.abs(
    totalDeductibleAmount + totalCoinsuranceAmount + totalCopaymentAmount + totalNonCoveredAmount
  );
};

export const getRTACardInfo = (relatedData: RTARelatedData) => ({
  title: 'You have an upcoming bill',
  amount: `Amount: $${formatMoney(calculateRTACardAmount(relatedData))}`,
  date: `Date of Encounter: ${formatDate(relatedData?.claimInformation?.claimFromDate)}`,
  buttonText: 'View details'
});

export const getRTADetails = (rtas: any, id: any) => {
  const rta = rtas?.find((item: any) => item.id === id);
  if (!rta) return { basicInfoDetails: null, rtaInfoDetails: null };
  const relatedData: RTARelatedData = JSON.parse(rta?.relatedData);
  const {
    claimInformation,
    FacilityID,
    FinNumber,
    MRN,
    CLAIM_PLAN_NAME,
    externalReferenceNo
  } = relatedData;
  if (!claimInformation)
    return { basicInfoDetails: null, rtaInfoDetails: null, rtaBreakdown: null };

  const {
    totalChargeAmount = 0,
    totalPayableAmount = 0,
    totalDeductibleAmount = 0,
    totalCoinsuranceAmount = 0,
    totalCopaymentAmount = 0,
    totalNonCoveredAmount = 0,
    totalEligibleAmount = 0,
    claimLineInformation = []
  } = claimInformation;

  const providerName = () => {
    const name = claimInformation?.serviceProvider?.providerName;
    if (!name.trim()) return 'Intermountain Healthcare';

    return name.trim();
  };

  const basicInfo = {
    provider: providerName(),
    date: formatDate(claimInformation?.claimFromDate),
    encounterId: `${FacilityID}-${FinNumber}`,
    patientId: MRN,
    patientName: `${claimInformation?.memberInformation?.memberFirstName} ${claimInformation?.memberInformation?.memberLastName}`
  };

  const basicInfoDetails = {
    provider: basicInfo.provider,
    date: basicInfo.date,
    patientName: `Patient: ${basicInfo.patientName}`,
    patientId: `Account Number: 107-${basicInfo.patientId}`,
    encounterId: `Encounter Number: ${basicInfo.encounterId}`
  };

  const claimInfo = {
    totalChargeAmount,
    totalPayableAmount,
    totalAdjustmentAmount:
      totalChargeAmount -
      (totalDeductibleAmount +
        totalCoinsuranceAmount +
        totalCopaymentAmount +
        totalPayableAmount +
        totalNonCoveredAmount),
    yourResponsibility:
      totalDeductibleAmount + totalCoinsuranceAmount + totalCopaymentAmount + totalNonCoveredAmount
  };

  const rtaInfoDetails = [
    { left: 'Total charges:', right: `$${formatMoney(Math.abs(claimInfo?.totalChargeAmount))}` },
    {
      left: 'What your insurance covers:',
      right: `$${formatMoney(Math.abs(claimInfo?.totalPayableAmount))}`
    },
    {
      left: 'Adjustment:',
      right: `$${formatMoney(Math.abs(claimInfo?.totalAdjustmentAmount))}`
    },
    {
      left: 'What you owe:',
      right: `$${formatMoney(Math.abs(claimInfo?.yourResponsibility))}`
    }
  ];

  const rtaBreakdown = {
    // Itemized Claims
    claimLineInformation,
    totalChargeAmount: formatMoney(Math.abs(totalChargeAmount)),
    // Payments and Adjustments
    totalPayableAmount: formatMoney(Math.abs(totalPayableAmount)),
    totalAdjustmentAmount: formatMoney(Math.abs(claimInfo.totalAdjustmentAmount)),
    // Claims
    CLAIM_PLAN_NAME,
    externalReferenceNo,
    totalEligibleAmount: formatMoney(Math.abs(totalEligibleAmount))
  };

  return {
    basicInfoDetails,
    rtaInfoDetails,
    rtaBreakdown
  };
};

export const BILLLING_DUE_STATUS_TYPES: any = {
  DUE_NOW: {
    title: 'Due Now',
    color: Color.primary
  },
  PAST_DUE: {
    title: 'Past Due',
    color: Color.billDueOrange
  },
  DELINQUENT: {
    title: 'Over Due',
    color: Color.billDueOrange
  }
};
